import flattenDeep from 'lodash/flattenDeep';
import type { ChatNovelScript, Scene } from '~/app/_types/chat-script';
import { notUndefinedOrNull } from '~/utils/styles/type';
import {
  convertCharacter,
  convertCharacterWithPosition,
} from './character-converter';
import { convertToChatScriptOperation } from './operation-converter';
import { convertRGBA } from './rgba-converter';
import type { ITellerScript, PartialProtoImage } from './types';

export const convertToChatNovelScript = (
  script: ITellerScript,
  scriptImageList: PartialProtoImage[]
): ChatNovelScript => {
  const scene_list = convertToChatScriptscene_list(script, scriptImageList);
  const totalOpsCount = flattenDeep(
    scene_list.map((scene) => scene.ops)
  ).length;

  return {
    totalOpsCount: totalOpsCount,
    sceneList: convertToChatScriptscene_list(script, scriptImageList),
  };
};

const convertToChatScriptscene_list = (
  script: ITellerScript,
  scriptImageList: PartialProtoImage[]
): Scene[] => {
  const characterList =
    script.header?.character_list
      ?.map((character) => {
        if (!character.id || !character.name || !character.image_id) return;
        return convertCharacter(
          {
            id: character.id,
            name: character.name,
            image_id: character.image_id,
          },
          scriptImageList
        );
      })
      ?.filter(notUndefinedOrNull) ?? [];

  const sceneList =
    script.scene_list
      ?.map((scene) => {
        const backgroundImageUrl = scriptImageList.find(
          (image) => image.id === scene.image_id
        )?.serving_url;
        const backgroundColor = scene.color
          ? convertRGBA(scene.color)
          : undefined;
        const characterWithPositionList = characterList.map((character) => {
          return convertCharacterWithPosition(
            character,
            scene.main_character_id_list ?? []
          );
        });
        const ops =
          scene.ops
            ?.map((op) => {
              return convertToChatScriptOperation(
                op,
                scriptImageList,
                characterWithPositionList,
                scene.type
              );
            })
            ?.filter(notUndefinedOrNull) ?? [];

        const opsCount = ops.length;

        return {
          background: {
            imageUrl: backgroundImageUrl,
            color: backgroundColor,
          },
          ops: ops,
          opsCount,
        };
      })
      ?.filter(notUndefinedOrNull) ?? [];

  // eslint-disable-next-line unicorn/no-array-reduce
  const sceneListWithOperationIndex = sceneList.reduce<Scene[]>(
    (acc, scene) => {
      const currentOpsCount = acc.reduce(
        (acc, scene) => acc + scene.opsCount,
        0
      );
      const ops = scene.ops.map((op, index) => {
        return {
          ...op,
          index: currentOpsCount + index,
        };
      });
      return [...acc, { ...scene, ops: ops }];
    },
    []
  );

  return sceneListWithOperationIndex;
};
